/**
 * Mail import and sync (docs/features/mail-sync.md): bring a company's existing mail into Bundle Mail and keep it
 * flowing while the MX moves. Two kinds of connections, several per mailbox:
 *
 * - `google`: an admin connects the company's Google Workspace once (a service account with domain-wide delegation);
 *   every Google user is mapped to a Bundle mailbox (default: the same local part on one of the workspace's domains).
 *   Each mapped user's whole Gmail history is imported, then new mail keeps syncing (Gmail history) until stopped.
 * - `imap`: a member (of that mailbox) or an admin adds an IMAP account — host, port, TLS, username, app password —
 *   and picks folders; Bundle imports their history, then polls for new mail.
 *
 * Imported mail goes through the inbound path (threads join by Message-ID / In-Reply-To / References, original
 * dates, raw .eml kept), deduplicated by Message-ID per mailbox, and never notifies; mail that arrives while syncing
 * does, like any new mail.
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";
import { EmailAddress } from "./mail.ts";

export const MailSyncKind = z.enum(["google", "imap"]);
export type MailSyncKind = z.infer<typeof MailSyncKind>;

/**
 * Where a connection (or one Google user in it) is:
 * connecting (checking credentials, listing users) → listing (finding every message) → importing (history, oldest
 * first) → syncing (caught up; polling for new mail). `paused` by a person; `error` needs one (bad password, revoked
 * delegation) — the rest keeps going and transient failures are retried by themselves.
 */
export const MailSyncPhase = z.enum(["connecting", "listing", "importing", "syncing", "paused", "error"]);
export type MailSyncPhase = z.infer<typeof MailSyncPhase>;

export const ImapSecurity = z.enum(["tls", "starttls", "none"]);
export type ImapSecurity = z.infer<typeof ImapSecurity>;

/** Counters of a connection or account. `total` grows while listing; `done` counts imported + duplicates + failed. */
export const MailSyncProgress = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  /** Already in the mailbox (same Message-ID): skipped. */
  duplicates: z.number().int().nonnegative(),
  /** Messages that couldn't be imported (unparseable, too big). */
  failed: z.number().int().nonnegative(),
});
export type MailSyncProgress = z.infer<typeof MailSyncProgress>;

/**
 * Where a person's Google Calendar import is: waiting (not started: Calendar off, the calendar scope not granted yet,
 * or they aren't a member) → listing (counting their events) → importing → syncing (caught up; Google's sync token is
 * polled like mail). `error` needs an admin (the rest keeps going).
 */
export const CalendarImportPhase = z.enum(["waiting", "listing", "importing", "syncing", "error"]);
export type CalendarImportPhase = z.infer<typeof CalendarImportPhase>;

/** A person's Google Calendar import: "2 calendars · 1,248 events", "importing 412 of 930 events", "waiting". */
export const CalendarImportStatus = z.object({
  phase: CalendarImportPhase,
  /** Their own calendars imported (the primary one and the secondary ones they own; shared ones are skipped). */
  calendars: z.number().int().nonnegative(),
  /** Events found (grows while listing). */
  events: z.number().int().nonnegative(),
  /** Events imported so far. */
  imported: z.number().int().nonnegative(),
  lastError: z.string().optional(),
  lastSyncAt: Timestamp.optional(),
});
export type CalendarImportStatus = z.infer<typeof CalendarImportStatus>;

/** One Google Workspace user of a `google` connection. */
export const MailSyncAccount = z.object({
  email: EmailAddress,
  name: z.string().optional(),
  /** The Bundle mailbox their mail goes to; absent = not imported. */
  mailboxId: Id.optional(),
  /** While the connection is in review: the mailbox Bundle proposes (same address, else same name). */
  suggestedMailboxId: Id.optional(),
  phase: MailSyncPhase,
  progress: MailSyncProgress,
  lastError: z.string().optional(),
  lastSyncAt: Timestamp.optional(),
  /** The user's other Google addresses (Directory aliases; Google's internal *.test-google-a.com ones left out). */
  aliases: z.array(EmailAddress).optional(),
  /** Gmail's "Send mail as" addresses (the primary included); `default` is the one Gmail sends from. */
  sendAs: z.array(z.object({ address: EmailAddress, name: z.string().optional(), default: z.boolean().optional() })).optional(),
  /** Addresses that couldn't be routed to the mailbox because their domain isn't one of the workspace's yet. */
  needsDomain: z.array(z.string()).optional(),
  /**
   * Mapped to a shared mailbox: who Google lets read it, read with the directory. `readers`: accepted Gmail delegates
   * (or, with none, the members of `group`, the Google Group with the same address); `granted`: of them, members here
   * who can read the mailbox; `pending`: not members yet (they get access when they join). `readers` 0 = Google lists
   * no delegates (shared by password, say): only admins read it. `unreadable`: Google wouldn't list its delegates.
   */
  readers: z.object({ readers: z.number().int().nonnegative(), granted: z.number().int().nonnegative(), pending: z.number().int().nonnegative(), group: EmailAddress.optional(), unreadable: z.boolean().optional() }).optional(),
  /** Their Google Calendar import (absent while the connection has Calendar off). */
  calendar: CalendarImportStatus.optional(),
});
export type MailSyncAccount = z.infer<typeof MailSyncAccount>;

export const ImapFolder = z.object({
  path: z.string().max(1000),
  /** RFC 6154 role (\Inbox, \Sent, \Junk, \Trash, \Archive, \All, \Drafts, \Flagged). */
  specialUse: z.string().optional(),
  messages: z.number().int().nonnegative().optional(),
});
export type ImapFolder = z.infer<typeof ImapFolder>;

export const MailSyncConnection = z.object({
  id: Id,
  kind: MailSyncKind,
  /** "Google Workspace (acme.com)", "ana@old-host.com". */
  label: z.string(),
  /** IMAP: the mailbox it fills. Google: per account. */
  mailboxId: Id.optional(),
  createdBy: Id,
  createdAt: Timestamp,
  phase: MailSyncPhase,
  /** Sum over the accounts (Google) or the account's own (IMAP). */
  progress: MailSyncProgress,
  lastError: z.string().optional(),
  lastSyncAt: Timestamp.optional(),
  imap: z
    .object({
      host: z.string(),
      port: z.number().int(),
      security: ImapSecurity,
      username: z.string(),
      /** Folders imported (paths). */
      folders: z.array(z.string()),
    })
    .optional(),
  google: z
    .object({
      /** The admin the directory is read as. */
      adminEmail: EmailAddress,
      /** The service account whose key was given, or Bundle's own. */
      serviceAccount: z.string(),
      /**
       * Waiting for the admin to choose whose mail to import (a new connection lists the directory and proposes a
       * mailbox per person; nothing is imported until `start`).
       */
      review: z.boolean().optional(),
      accounts: z.array(MailSyncAccount),
      /**
       * The Google Workspace's domains (Directory domains.list when its scope is granted, else derived from the users'
       * addresses); `aliasOf` = Google's domain alias of that domain.
       */
      domains: z.array(z.object({ domain: z.string(), aliasOf: z.string().optional(), primary: z.boolean().optional(), import: z.boolean() })).optional(),
      /** The admin saved the Domains step (`domains[].import` is their choice; otherwise it is the proposal: primary + its aliases). */
      domainsChosen: z.boolean().optional(),
      /** Chosen domains that aren't the workspace's mail domains (yet): their addresses are skipped until they are. */
      needsDomain: z.array(z.string()).optional(),
      /** Google Groups found (distribution lists): imported as mail groups when their domain is the workspace's. */
      groups: z
        .array(z.object({ address: EmailAddress, name: z.string().optional(), members: z.number().int().nonnegative(), whoCanPost: z.enum(["anyone", "organization", "members"]), needsDomain: z.boolean().optional() }))
        .optional(),
      /** Optional scopes not granted yet (domain-wide delegation): what they'd add is skipped, nothing fails. */
      missingScopes: z.array(z.string()).optional(),
      /**
       * "What to import" → Calendar (on by default): everyone's own calendars (a year back and everything ahead) are
       * imported into Bundle's calendar and kept in sync. Needs the calendar.readonly scope (`missingScopes` names it
       * until it's granted; mail goes on meanwhile). Off stops the sync and keeps what was imported.
       */
      calendar: z.boolean().optional(),
    })
    .optional(),
});
export type MailSyncConnection = z.infer<typeof MailSyncConnection>;

// ---------------------------------------------------------------------------------------------------------------
// Requests

const ImapServer = z.object({
  host: z.string().min(1).max(253),
  port: z.number().int().min(1).max(65535),
  security: ImapSecurity,
  username: z.string().min(1).max(320),
  /** An app password (Gmail, Outlook, iCloud and Yahoo need one with 2-step verification). Sealed at rest. */
  password: z.string().min(1).max(1000),
});

/** Try an IMAP account before saving it: → its folders. */
export const ProbeImapRequest = ImapServer;
export type ProbeImapRequest = z.infer<typeof ProbeImapRequest>;
export const ProbeImapResponse = z.object({ folders: z.array(ImapFolder) });
export type ProbeImapResponse = z.infer<typeof ProbeImapResponse>;

export const CreateMailSyncRequest = z.discriminatedUnion("kind", [
  ImapServer.extend({
    kind: z.literal("imap"),
    mailboxId: Id,
    /** Paths to import; default: every folder except Drafts (logic/mailSync defaultImapFolders). */
    folders: z.array(z.string().max(1000)).max(200).optional(),
  }),
  z.object({
    kind: z.literal("google"),
    /** A Google Workspace super admin (the directory is read as them). */
    adminEmail: EmailAddress,
    /** The service account key (JSON) with domain-wide delegation; omitted = Bundle's own service account. */
    serviceAccountKey: z.string().max(10_000).optional(),
  }),
]);
export type CreateMailSyncRequest = z.infer<typeof CreateMailSyncRequest>;

export const UpdateMailSyncRequest = z.object({
  /** Pause or resume the whole connection. */
  paused: z.boolean().optional(),
  /** IMAP: a new app password (an `error` connection starts again). */
  password: z.string().min(1).max(1000).optional(),
  /** IMAP: the folders to import (added ones are imported, removed ones stop syncing). */
  folders: z.array(z.string().max(1000)).max(200).optional(),
  /** Google: map users to mailboxes (null = don't import). */
  mapping: z.array(z.object({ email: EmailAddress, mailboxId: Id.nullable() })).max(5000).optional(),
  /**
   * Google (admins): make a mailbox for each of these users — their address's name on `domainId` (one that receives)
   * — and import into it. It is the person's own mailbox when they are an active member with that address; otherwise a
   * shared mailbox nobody reads yet, until an admin adds them (they haven't joined).
   */
  create: z.array(z.object({ email: EmailAddress, domainId: Id })).max(5000).optional(),
  /** Google: the review is done — import what is mapped (applied after `mapping` and `create`). */
  start: z.boolean().optional(),
  /**
   * Google: read the directory again now (users, aliases, send-as, domains, groups) instead of waiting a day, and
   * check the scopes again ("Check again" after granting calendar.readonly).
   */
  refresh: z.boolean().optional(),
  /** Google: import calendars too ("What to import" → Calendar). false stops the calendar sync; imported events stay. */
  calendar: z.boolean().optional(),
  /**
   * Google: which Workspace domains to bring into Bundle (the Domains step). Checked ones are added as pending mail
   * domains (with `aliasOf`, a domain NAME) when missing, and their users, aliases, send-as and groups are imported;
   * unchecked ones stop importing new things (nothing is deleted).
   */
  google: z.object({ domains: z.array(z.object({ domain: z.string().max(253), import: z.boolean(), aliasOf: z.string().max(253).optional() })).max(100) }).optional(),
});
export type UpdateMailSyncRequest = z.infer<typeof UpdateMailSyncRequest>;

export const ListMailSyncResponse = z.object({
  connections: z.array(MailSyncConnection),
  /** Bundle's own service account, when configured: the client id an admin authorizes in the Google Admin console. */
  googleClientId: z.string().optional(),
});
export type ListMailSyncResponse = z.infer<typeof ListMailSyncResponse>;

export const MailSyncResponse = z.object({ connection: MailSyncConnection });
export type MailSyncResponse = z.infer<typeof MailSyncResponse>;
